/* Ядро локализации.
 *
 * Язык живёт в URL и только там: /en/home, /bg/module/module_1. Дублировать его
 * в persist-сторе нельзя — два источника разъезжаются, и потом не скажешь, кто
 * прав: ссылка, которую прислали человеку, или то, что он выбирал в прошлый раз.
 * Ссылка побеждает всегда.
 */
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { CATALOGS } from './catalog';
import { isTextMarker, TEXT_MARKER } from './key';

export const LANGS = {
  en: { label: 'English', htmlLang: 'en' },
  ru: { label: 'Русский', htmlLang: 'ru' },
  bg: { label: 'Български', htmlLang: 'bg' },
  uk: { label: 'Українська', htmlLang: 'uk' },
};

export const LANG_CODES = Object.keys(LANGS);
export const DEFAULT_LANG = 'en';
export const isLang = (code) => Object.prototype.hasOwnProperty.call(LANGS, code);

/** Язык браузера — только как первая догадка для голого "/", дальше решает URL. */
export const detectLang = () => {
  const wanted = typeof navigator === 'undefined' ? [] : navigator.languages || [navigator.language];
  for (const tag of wanted) {
    const code = String(tag || '').slice(0, 2).toLowerCase();
    if (isLang(code)) return code;
  }
  return DEFAULT_LANG;
};

const LangContext = createContext(DEFAULT_LANG);

export const LanguageProvider = ({ lang, children }) => {
  useEffect(() => {
    const catalog = CATALOGS[lang] || CATALOGS[DEFAULT_LANG];
    document.documentElement.lang = LANGS[lang]?.htmlLang || DEFAULT_LANG;
    // вкладка и описание живут в index.html до загрузки скрипта, дальше их
    // ведёт каталог — иначе на болгарской странице осталась бы английская вкладка
    if (catalog['ui.meta.title']) document.title = catalog['ui.meta.title'];
    const meta = document.querySelector('meta[name="description"]');
    if (meta && catalog['ui.meta.description']) {
      meta.setAttribute('content', catalog['ui.meta.description']);
    }
  }, [lang]);
  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

/** Подставляет {имя} в строку. Нужна именно подстановка, а не склейка кусков:
 *  порядок слов у языков разный — «Module "X" complete» против «Модуль «X»
 *  завершён», и склейка префикса с суффиксом ломается на первом же языке,
 *  где слово встаёт в другое место. */
const fill = (template, params) =>
  params
    ? template.replace(/\{(\w+)\}/g, (whole, name) =>
        Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
      )
    : template;

/** t('ключ') -> строка. Промах откатывается на английский, а не на голый ключ:
 *  это публичное демо, и увидеть 'nav.home' на экране хуже, чем чужой язык. */
export const useT = () => {
  const lang = useLang();
  return useCallback(
    (key, params) => {
      const catalog = CATALOGS[lang] || CATALOGS[DEFAULT_LANG];
      const value = catalog[key];
      if (typeof value === 'string') return fill(value, params);
      const fallback = CATALOGS[DEFAULT_LANG][key];
      return typeof fallback === 'string' ? fill(fallback, params) : key;
    },
    [lang],
  );
};

/** Подставляет строки вместо маркеров K(...) во всей структуре данных. */
export const hydrate = (node, t) => {
  if (Array.isArray(node)) return node.map((item) => hydrate(item, t));
  if (node !== null && typeof node === 'object') {
    if (isTextMarker(node)) return t(node[TEXT_MARKER]);
    const out = {};
    for (const key of Object.keys(node)) out[key] = hydrate(node[key], t);
    return out;
  }
  return node;
};

/** Данные из moduleData/learningData, уже локализованные под текущий язык. */
export const useLocalized = (data) => {
  const t = useT();
  return useMemo(() => (data == null ? data : hydrate(data, t)), [data, t]);
};

/** Навигация внутри текущего языка: to('/home') -> /bg/home. */
export const useLangNavigate = () => {
  const navigate = useNavigate();
  const lang = useLang();
  return useCallback(
    (to, options) => {
      if (typeof to !== 'string') return navigate(to, options);
      return navigate(`/${lang}${to.startsWith('/') ? to : `/${to}`}`, options);
    },
    [navigate, lang],
  );
};

/** Смена языка сохраняет текущий экран: /bg/progress -> /uk/progress. */
export const useSwitchLang = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  return useCallback(
    (next) => {
      if (!isLang(next)) return;
      const rest = location.pathname.replace(new RegExp(`^/${lang}(?=/|$)`), '');
      navigate(`/${next}${rest}${location.search}`, { replace: true });
    },
    [navigate, location, lang],
  );
};
